import { useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Button, cn, useDialogFocus, useModalPresence } from "@dearbill/shared";

/**
 * Portal-mounted confirmation dialog. It mirrors `NewOrgModal`'s mechanics:
 * `createPortal` to `document.body`, `useDialogFocus` for the scroll lock,
 * focus and Tab wrap while `open`, `useModalPresence` for the open and close
 * motion, and `role="dialog"`, `aria-modal` and `aria-labelledby`. As in
 * `NewOrgModal`, only the Cancel button dismisses it; there is no backdrop or
 * Escape dismissal. The partner SPA has no i18n, so all copy arrives as plain
 * nodes.
 *
 * The parent owns what "confirm" does and toggles `open`; the dialog stays
 * mounted through its exit, inert, and returns focus to its opener as soon as
 * the close starts. Cancel is the first control, so it takes focus on open and
 * a stray Enter doesn't fire a consequential confirm. While `busy`, both
 * buttons disable and Confirm shows `busyLabel`, so an in-flight action can't
 * be fired twice or cancelled midway.
 */
export interface ConfirmModalProps {
  open: boolean;
  title: string;
  /** Body copy: plain text or richer nodes. */
  children: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Disables both buttons and shows `busyLabel` on Confirm while an action runs. */
  busy?: boolean;
  busyLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModal({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel = "Cancel",
  busy = false,
  busyLabel,
  onConfirm,
  onCancel,
}: ConfirmModalProps): ReactNode {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const { mounted, closing, onAnimationEnd } = useModalPresence(open);
  useDialogFocus({ open, panelRef });

  if (!mounted) return null;

  const node = (
    <div
      className={cn(
        "bg-midnight/30 fixed inset-0 z-50 flex items-center justify-center px-4 py-6",
        open ? "motion-safe:animate-modal-backdrop-in" : "motion-safe:animate-modal-backdrop-out",
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      inert={closing}
      aria-hidden={closing || undefined}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={cn(
          "w-full max-w-md rounded-3xl bg-white p-6 shadow-xl",
          open ? "motion-safe:animate-modal-panel-in" : "motion-safe:animate-modal-panel-out",
        )}
        onAnimationEnd={onAnimationEnd}
      >
        <h2 id={titleId} className="text-midnight font-serif text-xl font-normal leading-[1.5]">
          {title}
        </h2>
        <div className="text-slate-teal mt-2 text-sm">{children}</div>

        <div className="mt-6 flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              if (open) onCancel();
            }}
            disabled={busy}
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={() => {
              if (open) onConfirm();
            }}
            disabled={busy}
          >
            {busy && busyLabel ? busyLabel : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
